import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Check, X, AlertTriangle, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { breadcrumbSchema, faqPageSchema, localizedHead, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";
import { HeroImage } from "@/components/hero-image";
import { retaVsOzempicDeImage, retaVsOzempicEnImage } from "@/lib/responsive-images";

export const Route = createFileRoute("/reta-vs-ozempic")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const base = localizedHead("/reta-vs-ozempic", loaderData?.lang);
    const lang = loaderData?.lang ?? "en";
    const de = lang === "de";

    const faqs = de
      ? [
          { q: "Ist Reta besser als Ozempic?", a: "Reta (Retatrutide) gilt als potenziell stärkere nächste Generation, weil es als Triple-Agonist auf GLP-1, GIP und Glucagon wirkt, während Ozempic (Semaglutid) nur GLP-1 aktiviert. Studien deuten auf einen deutlich höheren Gewichtsverlust hin." },
          { q: "Was ist der Unterschied zwischen Retatrutide und Semaglutide?", a: "Semaglutide (Ozempic, Wegovy) ist ein GLP-1-Mono-Agonist. Retatrutide (Reta, LY3437943) ist ein Triple-Agonist, der zusätzlich GIP und Glucagon anspricht und dadurch stärker in den Energiestoffwechsel eingreift." },
          { q: "Ist Retatrutide stärker als Ozempic?", a: "In den bisher veröffentlichten Studien zeigt Retatrutide einen deutlich höheren durchschnittlichen Gewichtsverlust als Semaglutid (Ozempic). Der Triple-Agonist-Ansatz kombiniert Appetitkontrolle mit stärkerer metabolischer Wirkung." },
          { q: "Welche Nebenwirkungen hat Retatrutide im Vergleich zu Ozempic?", a: "Wie bei Ozempic dominieren gastrointestinale Nebenwirkungen: Übelkeit, Erbrechen, Durchfall, Verstopfung. Da Retatrutide breiter wirkt, sind Langzeit-Sicherheitsdaten besonders wichtig und werden aktuell in Phase-III-Studien erhoben." },
          { q: "Wann kommt Retatrutide auf den Markt?", a: "Retatrutide befindet sich im TRIUMPH-Phase-III-Programm. Eine reguläre Zulassung wird für 2026/27 erwartet. Zugang ist aktuell nur über genehmigte klinische Studienprogramme möglich." },
          { q: "Kann ich Retatrutide kaufen?", a: "Retatrutide ist noch nicht als Fertigarzneimittel zugelassen. Angebote unter Stichworten wie 'Retatrutide kaufen' oder 'Reta Peptid online' sind mit erheblichen Risiken (Qualität, Reinheit, Dosierung, Fälschungen) verbunden und rechtlich problematisch." },
        ]
      : [
          { q: "Is Reta better than Ozempic?", a: "Reta (Retatrutide) is seen as the potentially stronger next generation because it acts as a triple agonist on GLP-1, GIP and glucagon, while Ozempic (Semaglutide) only activates GLP-1. Trials suggest markedly higher weight loss." },
          { q: "What is the difference between Retatrutide and Semaglutide?", a: "Semaglutide (Ozempic, Wegovy) is a GLP-1 mono-agonist. Retatrutide (Reta, LY3437943) is a triple agonist that also targets GIP and glucagon and therefore engages energy metabolism more strongly." },
          { q: "Is Retatrutide stronger than Ozempic?", a: "In published trials, Retatrutide shows a markedly higher average weight loss than Semaglutide (Ozempic). The triple-agonist approach combines appetite control with a broader metabolic effect." },
          { q: "What are the side effects of Retatrutide vs Ozempic?", a: "As with Ozempic, gastrointestinal effects dominate: nausea, vomiting, diarrhea, constipation. Because Retatrutide acts more broadly, long-term safety data is especially important and is being collected in the ongoing Phase III programme." },
          { q: "When will Retatrutide be available?", a: "Retatrutide is in the TRIUMPH Phase III programme. A regular approval is expected around 2026/27. Access is currently only available through approved clinical study programmes." },
          { q: "Can I buy Retatrutide?", a: "Retatrutide is not yet approved as a finished pharmaceutical. Offers under keywords like 'buy Retatrutide' or 'Reta peptide online' carry significant risks (quality, purity, dosing, counterfeits) and are legally problematic." },
        ];

    const faqPage = faqPageSchema("/reta-vs-ozempic", loaderData?.lang, faqs);


    const article = {
      "@context": "https://schema.org",
      "@type": "Article",
      inLanguage: de ? "de-DE" : "en-US",
      headline: de
        ? "Reta vs Ozempic – Warum Retatrutide als stärkere nächste Generation gilt"
        : "Reta vs Ozempic – Why Retatrutide is the stronger next generation",
      about: [
        { "@type": "Drug", name: "Retatrutide", alternateName: ["Reta", "LY3437943"] },
        { "@type": "Drug", name: "Semaglutide", alternateName: ["Ozempic", "Wegovy"] },
      ],
    };

    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/reta-vs-ozempic", loaderData?.lang)) },
        { type: "application/ld+json", children: JSON.stringify(article) },
        { type: "application/ld+json", children: JSON.stringify(faqPage) },
      ],
    };
  },
  component: Page,
});

function Section({ eyebrow, title, children }: { eyebrow?: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-14">
      {eyebrow ? (
        <p className="text-[11px] font-semibold uppercase tracking-widest text-indigo">{eyebrow}</p>
      ) : null}
      <h2 className="mt-2 font-display text-2xl leading-tight text-foreground md:text-3xl">{title}</h2>
      <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function Page() {
  const { lang } = useI18n();
  const de = lang === "de";
  const img = de ? retaVsOzempicDeImage : retaVsOzempicEnImage;

  const alt = de
    ? "Reta vs Ozempic – Studienvergleich: Retatrutide 28,3 % vs Semaglutid 14,9 % durchschnittlicher Gewichtsverlust"
    : "Reta vs Ozempic – study comparison: Retatrutide 28.3% vs Semaglutide 14.9% average weight loss";

  const c = de
    ? {
        eyebrow: "Reta vs Ozempic",
        h1a: "Warum ",
        h1em: "Reta",
        h1b: " als die stärkere nächste Generation gegenüber Ozempic gilt",
        lead: "Ozempic hat den Markt für moderne Abnehm- und Stoffwechselbehandlungen massiv verändert. Retatrutide (Reta, LY3437943) geht als Triple-Agonist einen Schritt weiter – und gilt für viele als potenziell stärkere Weiterentwicklung.",
      }
    : {
        eyebrow: "Reta vs Ozempic",
        h1a: "Why ",
        h1em: "Reta",
        h1b: " is seen as the stronger next generation beyond Ozempic",
        lead: "Ozempic transformed modern weight-management and metabolic treatment. Retatrutide (Reta, LY3437943) goes further as a triple agonist — and is seen by many as the potentially stronger evolution.",
      };

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:py-20">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">{c.eyebrow}</p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {c.h1a}
          <em className="not-italic text-indigo">{c.h1em}</em>
          {c.h1b}
        </h1>
        <p className="mt-5 text-lg text-muted-foreground">{c.lead}</p>
      </motion.div>

      <HeroImage {...img} alt={alt} eager className="mt-8 max-w-2xl mx-auto" />

      {de ? (
        <>
          <Section title="Retatrutide: Das Peptid, über das aktuell alle sprechen">
            <p>
              Retatrutide, auch bekannt als <strong>Reta Peptid</strong> oder <strong>LY3437943</strong>, gehört zu den spannendsten neuen Wirkstoffen im Bereich Gewichtsmanagement, Stoffwechsel und moderner Peptidforschung. Besonders interessant ist Retatrutide, weil es nicht nur auf einen, sondern gleich auf drei wichtige Signalwege im Körper wirkt: <strong>GLP-1, GIP und Glucagon</strong>. Deshalb wird es auch als Triple-Agonist bezeichnet.
            </p>
            <p>
              Für viele wirkt Retatrutide dadurch nicht wie eine kleine Verbesserung, sondern wie ein komplett neuer Schritt in der Entwicklung moderner Stoffwechseltherapien.
            </p>
            <p>
              Der besondere Ansatz von Retatrutide liegt darin, dass es mehrere Prozesse gleichzeitig beeinflussen kann. Es kann die Sättigung unterstützen, das Hungergefühl reduzieren und den Körper dabei beeinflussen, Energie anders zu verwerten. Genau deshalb suchen viele nach Begriffen wie <em>Retatrutide Wirkung, Reta Peptid, Retatrutide Gewichtsverlust, Retatrutide Abnehmen, Retatrutide Erfahrungen, Retatrutide vs Tirzepatide</em> oder <em>Retatrutide vs Semaglutide</em>.
            </p>
          </Section>

          <Section eyebrow="Der Kernunterschied" title="Reta vs Ozempic: Der wichtigste Unterschied">
            <p>
              Ozempic enthält <strong>Semaglutid</strong>, einen GLP-1-Rezeptoragonist. GLP-1 beeinflusst Sättigung, Insulinfreisetzung, Blutzucker und Magenentleerung. Deshalb suchen viele nach <em>Ozempic Wirkung, GLP-1 Abnehmspritze, Ozempic Sättigung</em> oder <em>Ozempic Gewichtsverlust</em>.
            </p>
            <p>
              Retatrutide geht weiter. Reta wirkt nicht nur auf GLP-1, sondern zusätzlich auf <strong>GIP</strong> und <strong>Glucagon</strong>. Dadurch wird Retatrutide als <strong>GLP-1 GIP Glucagon Agonist</strong> oder Triple-Agonist Peptid beschrieben.
            </p>
            <p className="rounded-xl border border-indigo/30 bg-indigo/5 p-4 text-foreground">
              Einfach gesagt: <strong>Ozempic arbeitet mit einem Hauptsignal. Reta arbeitet mit drei Signalen.</strong>
            </p>
            <p>
              Das bedeutet nicht automatisch, dass Reta für jeden Menschen besser ist. Aber es erklärt, warum Retatrutide aktuell als potenziell stärkere und modernere Weiterentwicklung gesehen wird.
            </p>
          </Section>

          <Section eyebrow="Gewichtsverlust" title="Warum Reta beim Gewichtsverlust so viel Aufmerksamkeit bekommt">
            <p>
              Ozempic wurde bekannt, weil viele Menschen unter Semaglutid weniger Hunger verspüren, schneller satt werden und dadurch Gewicht verlieren. Retatrutide wird deshalb so stark diskutiert, weil Studiendaten auf einen besonders hohen Gewichtsverlust hindeuten – in klinischen Untersuchungen mit sehr deutlichen Veränderungen des Körpergewichts.
            </p>
            <p>
              Der große Unterschied liegt in der Breite des Ansatzes. Ozempic kann über GLP-1 helfen, Hunger und Appetit zu regulieren. Reta könnte durch die zusätzliche GIP- und Glucagon-Komponente nicht nur die Sättigung beeinflussen, sondern auch stärker in den Energiestoffwechsel eingreifen.
            </p>
            <p className="font-semibold text-foreground">Reta ist nicht einfach „ein neues Ozempic". Reta ist ein anderer, breiterer Ansatz.</p>
          </Section>

          <Section eyebrow="Wirkmechanismus" title="Warum Reta als stärkerer Ansatz gilt">
            <div className="grid gap-3 md:grid-cols-3">
              {[
                { t: "GLP-1", b: "Sättigung, Appetitkontrolle, Blutzuckerregulation, verlangsamte Magenentleerung." },
                { t: "GIP", b: "Weitere Effekte auf Insulinantwort, Fettstoffwechsel und metabolische Regulation." },
                { t: "Glucagon", b: "Energieverbrauch, Leberstoffwechsel und metabolische Aktivität." },
              ].map((x) => (
                <div key={x.t} className="card-elevated p-5">
                  <p className="font-mono text-sm font-semibold text-indigo">{x.t}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{x.b}</p>
                </div>
              ))}
            </div>
            <p>
              Die Kombination aus GLP-1, GIP und Glucagon macht Retatrutide zu einem Triple-Agonist. Das ist der entscheidende Punkt im Vergleich zu Ozempic: <strong>Ozempic ist GLP-1. Reta ist GLP-1 plus GIP plus Glucagon.</strong>
            </p>
          </Section>

          <Section eyebrow="Appetit" title="Reta vs Ozempic beim Appetit">
            <p>
              Ozempic ist vor allem bekannt, weil viele Anwender von weniger Hunger, schnellerer Sättigung und weniger Essdruck berichten. Retatrutide könnte ebenfalls stark auf Appetit und Sättigung wirken, aber durch den Triple-Agonist-Ansatz zusätzlich eine breitere metabolische Wirkung entfalten.
            </p>
            <p>
              Der Unterschied ist nicht nur „weniger essen". Der Unterschied ist die mögliche Kombination aus weniger Appetit, verändertem Energieverbrauch und stärkerer Stoffwechselaktivierung.
            </p>
          </Section>

          <Section eyebrow="Stoffwechsel" title="Reta vs Ozempic beim Stoffwechsel">
            <p>
              Ozempic ist stark mit Blutzuckerregulation und Typ-2-Diabetes verbunden. Retatrutide wird ebenfalls bei Stoffwechselthemen untersucht, aber mit einem breiteren Profil – neben Gewichtsverlust auch Blutzucker, A1C, metabolische Gesundheit, Fettleber (MASLD/MASH), Schlafapnoe und Osteoarthritis.
            </p>
            <p>
              Das macht Reta für Menschen interessant, die nicht nur Gewichtsverlust suchen, sondern einen umfassenderen Ansatz im Bereich Adipositas, Stoffwechsel, Blutzucker, viszerales Fett und kardiometabolische Gesundheit.
            </p>
          </Section>

          <Section eyebrow="Sicherheit" title="Reta vs Ozempic bei Nebenwirkungen">
            <p>
              Ozempic kann Nebenwirkungen verursachen: Übelkeit, Erbrechen, Durchfall, Verstopfung, Bauchschmerzen, Gallenblasen-Ereignisse, seltene Pankreatitis. Auch Reta ist nicht frei von Risiken. Gerade weil Retatrutide stärker und breiter wirken könnte, sind Sicherheitsfragen besonders wichtig.
            </p>
            <p className="rounded-xl border border-warning/30 bg-warning/5 p-4 text-foreground">
              <AlertTriangle className="mr-2 inline h-4 w-4 text-warning" />
              Retatrutide ist kein Nahrungsergänzungsmittel und kein gewöhnliches Peptid für unkontrollierte Selbstanwendung. Es ist ein medizinischer Forschungswirkstoff, der in klinischen Studien untersucht wird.
            </p>
            <p>
              Stärker bedeutet nicht automatisch besser für jeden. Wirkung, Verträglichkeit, Dosierung, Sicherheit und Langzeitdaten müssen sehr genau bewertet werden.
            </p>
          </Section>

          <Section eyebrow="Marktvergleich" title="Reta vs Mounjaro, Wegovy und Ozempic">
            <p>
              Ozempic und Wegovy basieren auf Semaglutid. Mounjaro und Zepbound basieren auf Tirzepatid, das bereits dual über GIP und GLP-1 wirkt. Reta kombiniert GIP, GLP-1 und Glucagon – ein Schritt weiter.
            </p>
            <div className="mt-4 overflow-hidden rounded-2xl border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Wirkstoff</th>
                    <th className="px-4 py-3 font-medium">Marke</th>
                    <th className="px-4 py-3 font-medium">Mechanismus</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border">
                    <td className="px-4 py-3 font-semibold text-foreground">Semaglutid</td>
                    <td className="px-4 py-3 text-muted-foreground">Ozempic / Wegovy</td>
                    <td className="px-4 py-3">GLP-1</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="px-4 py-3 font-semibold text-foreground">Tirzepatid</td>
                    <td className="px-4 py-3 text-muted-foreground">Mounjaro / Zepbound</td>
                    <td className="px-4 py-3">GLP-1 + GIP (Dual)</td>
                  </tr>
                  <tr className="border-t border-border bg-emerald/5">
                    <td className="px-4 py-3 font-semibold text-foreground">Retatrutide (Reta)</td>
                    <td className="px-4 py-3 text-muted-foreground">LY3437943</td>
                    <td className="px-4 py-3 font-semibold text-emerald">GLP-1 + GIP + Glucagon (Triple)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section eyebrow="Realistische Einordnung" title="Warum Reta kein einfacher Ozempic-Ersatz ist">
            <p>
              Ozempic ist ein zugelassenes Arzneimittel mit klarer Indikation, bekannten Warnhinweisen und ärztlicher Anwendung. Retatrutide befindet sich noch im Forschungs- und Zulassungsprozess. Wer nach <em>Retatrutide kaufen, Reta Peptid kaufen, Retatrutide ohne Rezept</em> oder <em>buy Retatrutide</em> sucht, bewegt sich in einem Bereich mit erheblichen Risiken.
            </p>
            <p>
              Viele Online-Angebote können ungeprüft, falsch dosiert, verunreinigt oder gefälscht sein. Seriöser ist ein Aufklärungsansatz und der Zugang über genehmigte klinische Programme oder eine ärztlich begleitete Behandlung in einer Partnerklinik.
            </p>
          </Section>

          <Section eyebrow="Fazit" title="Warum Reta als bessere nächste Generation gilt">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="card-elevated p-5">
                <p className="font-semibold text-foreground">Ozempic</p>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Bekannt, bewährt, breit verfügbar</li>
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Etablierte GLP-1-Wirkung</li>
                  <li className="flex gap-2"><X className="mt-0.5 h-4 w-4 flex-none text-muted-foreground" />Nur ein Signalweg (GLP-1)</li>
                </ul>
              </div>
              <div className="card-elevated border-emerald/30 p-5">
                <p className="font-semibold text-emerald">Reta (Retatrutide)</p>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Triple-Agonist: GLP-1 + GIP + Glucagon</li>
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Höherer Gewichtsverlust in Studien</li>
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Breiterer Stoffwechsel-Ansatz</li>
                </ul>
              </div>
            </div>
            <p>
              Ozempic war der Durchbruch. Reta geht einen Schritt weiter. Retatrutide kombiniert GLP-1, GIP und Glucagon in einem Triple-Agonist-Ansatz und gilt für viele als potenziell stärkere, breitere und modernere Weiterentwicklung.
            </p>
          </Section>
        </>
      ) : (
        <>
          <Section title="Retatrutide: the peptide everyone is talking about">
            <p>
              Retatrutide — also known as <strong>Reta peptide</strong> or <strong>LY3437943</strong> — is one of the most exciting new compounds in weight management, metabolism and modern peptide research. What makes it stand out is that it acts on three key signalling pathways at once: <strong>GLP-1, GIP and glucagon</strong>. This is why it's called a triple agonist.
            </p>
            <p>
              For many, Retatrutide feels less like an incremental improvement and more like a genuinely new step in modern metabolic therapy.
            </p>
          </Section>

          <Section eyebrow="The core difference" title="Reta vs Ozempic: the key difference">
            <p>
              Ozempic contains <strong>Semaglutide</strong>, a GLP-1 receptor agonist. GLP-1 influences satiety, insulin release, blood glucose and gastric emptying. Retatrutide goes further — it acts on GLP-1 <strong>plus GIP plus glucagon</strong>, making it a <strong>GLP-1 / GIP / glucagon agonist</strong> or triple-agonist peptide.
            </p>
            <p className="rounded-xl border border-indigo/30 bg-indigo/5 p-4 text-foreground">
              Put simply: <strong>Ozempic works with one main signal. Reta works with three.</strong>
            </p>
          </Section>

          <Section eyebrow="Weight loss" title="Why Reta is drawing so much attention on weight loss">
            <p>
              Ozempic became famous because many people on Semaglutide report less hunger, faster satiety and lower food drive. Retatrutide is being discussed so heavily because trial data points to particularly high weight loss — driven by a broader approach that combines appetite control with a stronger effect on energy metabolism.
            </p>
            <p className="font-semibold text-foreground">Reta isn't just "a new Ozempic". Reta is a different, broader approach.</p>
          </Section>

          <Section eyebrow="Mechanism" title="Why Reta is seen as the stronger approach">
            <div className="grid gap-3 md:grid-cols-3">
              {[
                { t: "GLP-1", b: "Satiety, appetite control, blood glucose regulation, slowed gastric emptying." },
                { t: "GIP", b: "Additional effects on insulin response, lipid metabolism and metabolic regulation." },
                { t: "Glucagon", b: "Energy expenditure, hepatic metabolism and metabolic activity." },
              ].map((x) => (
                <div key={x.t} className="card-elevated p-5">
                  <p className="font-mono text-sm font-semibold text-indigo">{x.t}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{x.b}</p>
                </div>
              ))}
            </div>
            <p>
              <strong>Ozempic is GLP-1. Reta is GLP-1 plus GIP plus glucagon.</strong>
            </p>
          </Section>

          <Section eyebrow="Metabolism" title="Reta vs Ozempic on metabolism">
            <p>
              Ozempic is closely linked to blood-glucose regulation and type-2 diabetes. Retatrutide is being studied across a broader metabolic profile — weight loss, A1C, fatty liver (MASLD/MASH), sleep apnea and osteoarthritis alongside cardiometabolic outcomes.
            </p>
          </Section>

          <Section eyebrow="Safety" title="Reta vs Ozempic side effects">
            <p>
              Ozempic can cause side effects: nausea, vomiting, diarrhea, constipation, abdominal pain, gallbladder events, rare pancreatitis. Reta is not risk-free either. Because Retatrutide may act more strongly and more broadly, safety questions matter even more.
            </p>
            <p className="rounded-xl border border-warning/30 bg-warning/5 p-4 text-foreground">
              <AlertTriangle className="mr-2 inline h-4 w-4 text-warning" />
              Retatrutide is not a supplement and not a casual self-use peptide. It is an investigational medical compound under clinical study.
            </p>
          </Section>

          <Section eyebrow="Market comparison" title="Reta vs Mounjaro, Wegovy and Ozempic">
            <p>
              Ozempic and Wegovy are based on Semaglutide. Mounjaro and Zepbound are based on Tirzepatide, which already acts dually on GIP and GLP-1. Reta combines GIP, GLP-1 and glucagon — one step further.
            </p>
            <div className="mt-4 overflow-hidden rounded-2xl border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Compound</th>
                    <th className="px-4 py-3 font-medium">Brand</th>
                    <th className="px-4 py-3 font-medium">Mechanism</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border">
                    <td className="px-4 py-3 font-semibold text-foreground">Semaglutide</td>
                    <td className="px-4 py-3 text-muted-foreground">Ozempic / Wegovy</td>
                    <td className="px-4 py-3">GLP-1</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="px-4 py-3 font-semibold text-foreground">Tirzepatide</td>
                    <td className="px-4 py-3 text-muted-foreground">Mounjaro / Zepbound</td>
                    <td className="px-4 py-3">GLP-1 + GIP (Dual)</td>
                  </tr>
                  <tr className="border-t border-border bg-emerald/5">
                    <td className="px-4 py-3 font-semibold text-foreground">Retatrutide (Reta)</td>
                    <td className="px-4 py-3 text-muted-foreground">LY3437943</td>
                    <td className="px-4 py-3 font-semibold text-emerald">GLP-1 + GIP + Glucagon (Triple)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section eyebrow="Realistic framing" title="Why Reta isn't just an Ozempic swap">
            <p>
              Ozempic is an approved drug with a defined indication, known warnings and physician-led use. Retatrutide is still in research and approval. Searches like <em>buy Retatrutide, Reta peptide online, Retatrutide without prescription</em> lead into a space with significant risks: unverified quality, incorrect dosing, contamination and counterfeits.
            </p>
          </Section>

          <Section eyebrow="Bottom line" title="Why Reta is seen as the better next generation">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="card-elevated p-5">
                <p className="font-semibold text-foreground">Ozempic</p>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Known, established, widely available</li>
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Proven GLP-1 effect</li>
                  <li className="flex gap-2"><X className="mt-0.5 h-4 w-4 flex-none text-muted-foreground" />Single signalling pathway (GLP-1)</li>
                </ul>
              </div>
              <div className="card-elevated border-emerald/30 p-5">
                <p className="font-semibold text-emerald">Reta (Retatrutide)</p>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Triple agonist: GLP-1 + GIP + glucagon</li>
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Higher weight loss in trials</li>
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-emerald" />Broader metabolic approach</li>
                </ul>
              </div>
            </div>
            <p>
              Ozempic was the breakthrough. Reta goes further. Retatrutide combines GLP-1, GIP and glucagon in a triple-agonist approach and is seen by many as the potentially stronger, broader and more modern next generation.
            </p>
          </Section>
        </>
      )}

      <div className="mt-12 rounded-2xl border border-indigo/30 bg-gradient-to-br from-indigo/10 via-background to-emerald/10 p-6 md:p-8">
        <h3 className="font-display text-2xl text-foreground">
          {de ? "Persönliche Beratung zu Retatrutide" : "Personal consultation on Retatrutide"}
        </h3>
        <p className="mt-2 text-muted-foreground">
          {de
            ? "Kostenloses Erstgespräch mit einem spezialisierten Ärzteteam – auf Deutsch oder Englisch."
            : "Free initial consultation with a specialised medical team – in English or German."}
        </p>
        <div className="mt-4">
          <ConsultCta />
        </div>
      </div>

      <RelatedLinks only={["/vergleich", "/wissenschaft", "/nebenwirkungen"]} />
    </div>
  );
}
